import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_ReferenceSelectors() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🎯 CSS Selectors</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS selectors are patterns used to select and style HTML elements. They
        define which elements the CSS rules apply to. Understanding selectors is
        key to writing clean, powerful, and maintainable stylesheets.
      </p>

      {/* 🔑 Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#1e3a8a" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Selectors target HTML elements based on <strong>names</strong>,
            <strong>attributes</strong>, <strong>classes</strong>, and{" "}
            <strong>relationships</strong>.
          </li>
          <li>
            Use multiple selectors to apply styles to more than one element.
          </li>
          <li>
            Specificity determines which selector “wins” when conflicts occur.
          </li>
          <li>
            Combine selectors with combinators and pseudo-classes for precise
            control.
          </li>
          <li>
            Universal selector <code>*</code> targets all elements.
          </li>
        </ul>
      </div>

      {/* 1️⃣ Basic Selectors */}
      <h2 className="section-title">1️⃣ Basic Selectors</h2>
      <CodeCopyBlock
        code={`/* Select by element name */
p { color: blue; }

/* Select by class */
.title { font-weight: bold; }

/* Select by ID */
#main { background-color: lightgray; }

/* Universal selector */
* { box-sizing: border-box; }`}
      />

      {/* 2️⃣ Attribute Selectors */}
      <h2 className="section-title">2️⃣ Attribute Selectors</h2>
      <CodeCopyBlock
        code={`/* Exact match */
input[type="text"] { border: 1px solid gray; }

/* Partial match (contains) */
a[href*="google"] { color: red; }

/* Starts with */
img[src^="icon"] { width: 40px; }

/* Ends with */
a[href$=".pdf"] { color: green; }`}
      />

      {/* 3️⃣ Combinator Selectors */}
      <h2 className="section-title">3️⃣ Combinators</h2>
      <CodeCopyBlock
        code={`/* Descendant (space) */
div p { color: blue; }

/* Child (>) */
div > p { color: green; }

/* Adjacent sibling (+) */
h2 + p { margin-top: 0; }

/* General sibling (~) */
h2 ~ p { color: gray; }`}
      />

      {/* 4️⃣ Grouping Selectors */}
      <h2 className="section-title">4️⃣ Grouping Selectors</h2>
      <CodeCopyBlock
        code={`/* Apply same style to multiple elements */
h1, h2, h3 {
  font-family: "Poppins", sans-serif;
  color: #333;
}`}
      />

      {/* 5️⃣ Pseudo-Classes */}
      <h2 className="section-title">5️⃣ Pseudo-Classes</h2>
      <CodeCopyBlock
        code={`/* User interaction */
a:hover { color: orange; }
a:active { color: red; }
a:visited { color: purple; }

/* Structural */
li:first-child { font-weight: bold; }
li:last-child { color: blue; }
li:nth-child(2) { color: green; }

/* Form states */
input:focus { border-color: #3b82f6; }
input:disabled { background: #f3f4f6; }`}
      />

      {/* 6️⃣ Pseudo-Elements */}
      <h2 className="section-title">6️⃣ Pseudo-Elements</h2>
      <CodeCopyBlock
        code={`/* Insert content before or after */
p::before { content: "👉 "; }
p::after { content: " ✅"; }

/* Style first letter or line */
p::first-letter { font-size: 1.5em; color: red; }
p::first-line { font-weight: bold; }`}
      />

      {/* 7️⃣ Negation & Advanced Selectors */}
      <h2 className="section-title">7️⃣ Negation & Advanced Selectors</h2>
      <CodeCopyBlock
        code={`/* Exclude specific element */
div:not(.highlight) { background: #f1f5f9; }

/* Target elements with a specific attribute pattern */
[class^="btn-"] { padding: 10px; }
[class*="-primary"] { background: blue; color: white; }`}
      />

      {/* 💡 Tips */}
      <div
        style={{
          background: "linear-gradient(135deg, #dcfce7, #bbf7d0)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#166534" }}>💡 Tips</h2>
        <ul className="tutorial-list-points">
          <li>Use class selectors for reusable styling.</li>
          <li>
            Avoid overusing ID selectors; they have higher specificity and can
            complicate overrides.
          </li>
          <li>
            Keep selectors short and meaningful to improve readability and
            maintainability.
          </li>
          <li>
            Test selectors using browser developer tools to see matched
            elements.
          </li>
        </ul>
      </div>

      {/* 🚀 Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <strong>CSS Specificity</strong> and how browsers
            resolve conflicting styles.
          </li>
          <li>
            Explore <code>:nth-child()</code> and <code>:not()</code> in complex
            layouts.
          </li>
          <li>
            Use <strong>attribute selectors</strong> for dynamic UI styling.
          </li>
          <li>
            Move to the <strong>CSS Reference</strong> section to explore all
            property categories.
          </li>
        </ul>
      </div>
    </div>
  );
}
